// services/chatApi.ts
//
// Client for the text-chat-with-Aleph `/chat/*` endpoints. All three require
// an authenticated user; we go through `authService.makeAuthenticatedRequest`
// which refreshes the token, sets Authorization/Content-Type, and returns a
// streamable Response (used for the SSE POST as well as the JSON GETs).

import { authService } from './authService';
import {
  SendMessageBody,
  StreamHandlers,
  ChatSessionsResponse,
  ChatThreadResponse,
  SessionEvent,
  DeltaEvent,
  DoneEvent,
  ErrorEvent,
} from '@/types/chat';

// Same base-URL convention as authService / booksApi. Empty in dev → relative
// URLs served through the Vite proxy (see the `/chat` proxy entry).
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/** Handle returned by `sendChatMessage` so callers can cancel a live stream. */
export interface ChatStreamHandle {
  /** Resolves when the stream finishes (done, error, or aborted). */
  done: Promise<void>;
  /** Aborts the in-flight request and stops parsing. */
  abort: () => void;
}

/**
 * Dispatch one parsed SSE frame (a block separated by a blank line) to the
 * matching handler. We only need to look at the `event:` and `data:` lines.
 */
function dispatchFrame(rawEvent: string, rawData: string, handlers: StreamHandlers): void {
  let data: unknown;
  try {
    data = JSON.parse(rawData);
  } catch {
    return; // ignore unparseable frames (e.g. SSE comments / keep-alives)
  }

  switch (rawEvent) {
    case 'session':
      handlers.onSession?.(data as SessionEvent);
      break;
    case 'delta':
      handlers.onDelta?.(data as DeltaEvent);
      break;
    case 'done':
      handlers.onDone?.(data as DoneEvent);
      break;
    case 'error':
      handlers.onError?.(data as ErrorEvent);
      break;
    default:
      break;
  }
}

/**
 * POST a message and stream the reply. Returns immediately with a handle; the
 * handlers fire as `session` / `delta` / `done` / `error` events arrive.
 *
 * Stream contract: exactly one `session` (first), zero or more `delta`, then
 * either one `done` or one `error` (last).
 */
export function sendChatMessage(body: SendMessageBody, handlers: StreamHandlers): ChatStreamHandle {
  const controller = new AbortController();

  const done = (async () => {
    let response: Response;
    try {
      response = await authService.makeAuthenticatedRequest(`${API_BASE_URL}/chat/messages`, {
        method: 'POST',
        headers: { Accept: 'text/event-stream' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (err) {
      if (!controller.signal.aborted) {
        handlers.onError?.({ message: (err as Error)?.message || 'Failed to reach the server.' });
      }
      return;
    }

    if (!response.ok || !response.body) {
      handlers.onError?.({ message: `Request failed (${response.status})` });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      // SSE frames are separated by a blank line ("\n\n"). Accumulate bytes,
      // split off complete frames, and parse each one's event/data lines.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;

        buffer += decoder.decode(value, { stream: true });

        let sep: number;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);

          let event = 'message';
          const dataLines: string[] = [];
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) {
              event = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              dataLines.push(line.slice(5).trimStart());
            }
          }
          if (dataLines.length > 0) {
            dispatchFrame(event, dataLines.join('\n'), handlers);
          }
        }
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        handlers.onError?.({ message: (err as Error)?.message || 'The connection dropped.' });
      }
    }
  })();

  return { done, abort: () => controller.abort() };
}

/** `GET /chat/sessions?book_id=...&order=time|position`. */
export async function fetchChatSessions(
  bookId: string,
  order: 'time' | 'position' = 'time'
): Promise<ChatSessionsResponse> {
  const params = new URLSearchParams({ book_id: bookId, order });
  const response = await authService.makeAuthenticatedRequest(
    `${API_BASE_URL}/chat/sessions?${params.toString()}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch chat sessions: ${response.statusText}`);
  }
  return response.json();
}

/** `GET /chat/sessions/{id}/messages`. */
export async function fetchSessionMessages(sessionId: number): Promise<ChatThreadResponse> {
  const response = await authService.makeAuthenticatedRequest(
    `${API_BASE_URL}/chat/sessions/${sessionId}/messages`,
    { method: 'GET' }
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch session ${sessionId}: ${response.statusText}`);
  }
  return response.json();
}

/** `DELETE /chat/sessions/{id}`: removes the conversation and its messages. */
export async function deleteChatSession(sessionId: number): Promise<void> {
  const response = await authService.makeAuthenticatedRequest(
    `${API_BASE_URL}/chat/sessions/${sessionId}`,
    { method: 'DELETE' }
  );
  if (!response.ok) {
    throw new Error(`Failed to remove conversation ${sessionId}: ${response.statusText}`);
  }
}

export type { SessionEvent, DeltaEvent, DoneEvent, ErrorEvent };
